import Swiper from 'swiper';
import { Autoplay } from 'swiper/modules';

/**
 * Fade Out method
 *
 * @param {string} el
 */
export function fadeOut(el) {
    if (!el) {
        throw Error('"fadeOut function - "You didn\'t add required parameters');
    }

    const domElement =
        el instanceof HTMLElement ? el : document.querySelector(el);

    if (!domElement) {
        throw Error("domElement doesn't exist");
    }

    domElement.style.opacity = 1;

    (function fade() {
        if ((domElement.style.opacity -= 0.1) < 0) {
            domElement.style.display = 'none';
        } else {
            requestAnimationFrame(fade);
        }
    })();
}

/**
 * Fade In method
 *
 * @param {string} el      - element that need to fade in
 *
 * @param {string} display - display variant
 */
export function fadeIn(el, display = 'block') {
    if (el === undefined) return;
    if (!el) {
        throw Error('"fadeIn function - "You didn\'t add required parameters');
    }

    const domElement = document.querySelector(el);

    if (!domElement) {
        throw Error("domElement doesn't exist");
    }

    domElement.style.opacity = 0;
    domElement.style.display = display || 'block';

    (function fade() {
        let val = parseFloat(domElement.style.opacity);
        if (!((val += 0.1) > 1)) {
            domElement.style.opacity = val;
            requestAnimationFrame(fade);
        }
    })();
}

/**
 *  Set equal height to selected elements calculated as bigger height
 *
 * @param {Array | string} elementsSelector  - selector for searching elements
 * @param {string} minOrMax          - Define what dimension should be calculated (minHeight or maxHeight)
 * @return {Array | string} elementsSelector
 */
export function equalHeights(elementsSelector, minOrMax = 'max') {
    if (!elementsSelector) {
        throw Error(
            '"equalHeights function - "You didn\'t add required parameters'
        );
    }

    const heights = [];
    const elementsSelectorArr = Array.isArray(elementsSelector)
        ? elementsSelector
        : [...document.querySelectorAll(elementsSelector)];

    elementsSelectorArr.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.style.height = 'auto';
    });

    elementsSelectorArr.forEach((item) => {
        heights.push(item.offsetHeight);
    });

    const commonHeight =
        minOrMax === 'max'
            ? Math.max.apply(0, heights)
            : Math.min.apply(0, heights);

    elementsSelectorArr.forEach((item) => {
        // eslint-disable-next-line no-param-reassign
        item.style.height = `${commonHeight}px`;
    });

    return elementsSelector;
}


/**
 * Check if element in viewport
 *
 * @param {HTMLElement | null} el
 * @param {number} offset - Adjustable offset value when element becomes visible
 * @return {boolean} Result of checking
 */
export function isInViewport(el, offset = 100) {
    if (!el) {
        throw Error(
            '"isInViewport function - "You didn\'t add required parameters'
        );
    }

    const scroll = window.scrollY || window.pageYOffset;
    const boundsTop = el.getBoundingClientRect().top + offset + scroll;

    const viewport = {
        top: scroll,
        bottom: scroll + window.innerHeight,
    };

    const bounds = {
        top: boundsTop,
        bottom: boundsTop + el.clientHeight,
    };

    return (
        (bounds.bottom >= viewport.top && bounds.bottom <= viewport.bottom) ||
        (bounds.top <= viewport.bottom && bounds.top >= viewport.top)
    );
}

/**
 * Debounce function
 *
 * @param {Function | null} fn  - function that should be executed
 * @param {number} delay        - time delay
 * @return {Function}           - function to execute
 */
export const debounce = (fn, delay = 1000) => {
    if (!fn) {
        throw Error(
            '"debounce function - "You didn\'t add required parameters'
        );
    }

    let timeout;

    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
    };
};

/**
 *
 * @param {Function | null} func  - function that should be executed
 * @param {number} ms             - time delay
 * @return {Function} wrapper     - function to execute
 */
export const throttle = (func, ms) => {
    let isThrottled = false;
    let savedArgs;
    let savedThis;

    function wrapper() {
        if (isThrottled) {
            // (2)
            // eslint-disable-next-line prefer-rest-params
            savedArgs = arguments;
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            savedThis = this;
            return;
        }

        // eslint-disable-next-line prefer-rest-params
        func && func.apply(this, arguments); // (1)

        isThrottled = true;

        setTimeout(() => {
            isThrottled = false; // (3)
            if (savedArgs) {
                wrapper.apply(savedThis, savedArgs);
                // eslint-disable-next-line no-multi-assign
                savedArgs = savedThis = null;
            }
        }, ms);
    }

    return wrapper;
};

/**
 * Copy to clipboard
 *
 * @param {HTMLElement | null} parent
 * @param {HTMLElement | null} element -  element that  contain value to copy
 */
export const copyToClipboard = (parent, element) => {
    if (!parent || !element) {
        throw Error(
            '"copyToClipboard function - "You didn\'t add required parameters'
        );
    }

    const el = document.createElement('textarea');
    el.value = element.value;
    document.body.appendChild(el);
    el.select();

    try {
        const successful = document.execCommand('copy');

        if (successful) {
            parent.classList.add('copied');

            setTimeout(() => {
                parent.classList.remove('copied');
            }, 3000);
        }
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Oops, unable to copy');
    }

    document.body.removeChild(el);
};

/**
 * Test value with regex
 *
 * @param {string} fieldType  - The allowed type of the fields
 * @param {string} value
 * @return {boolean} Result of checking
 */
export const validateField = (fieldType = null, value = null) => {
    if (!fieldType || !value) {
        throw Error(
            '"validateField function - "You didn\'t add required parameters'
        );
    }

    const phoneREGEX = /^[0-9+]{6,13}$/;
    const nameREGEX = /^[a-zA-Zа-яА-ЯёЁіІїЇєЄґҐ'’ʼ\s-]+$/;
    const postalREGEX = /^[A-Z]{1,2}[0-9]{1,2} ?[0-9][A-Z]{2}$/i;
    const emailREGEX = /^[\w+.-]+@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    const dummyREGEX = /^[a-zA-Z0-9]{2,30}$/;

    let checkResult = false;

    switch (fieldType) {
        case 'name':
        case 'first_name':
        case 'last_name':
            checkResult = nameREGEX.test(value);
            break;
        case 'phone':
            checkResult = phoneREGEX.test(value);
            break;
        case 'postal':
            checkResult = postalREGEX.test(value);
            break;
        case 'email':
            checkResult = emailREGEX.test(value);
            break;
        case 'price':
            checkResult = dummyREGEX.test(value);
            break;
        case 'aim':
            checkResult = dummyREGEX.test(value);
            break;
        case 'date':
            checkResult = dummyREGEX.test(value);
            break;
        case 'subject':
        case 'company':
            checkResult = dummyREGEX.test(value);
            break;
        default:
            checkResult = true;
            break;
    }

    return checkResult;
};

/**
 * Polyfill for closest method
 */
export function closestPolyfill() {
    if (window.Element && !Element.prototype.closest) {
        Element.prototype.closest = (s) => {
            const matches = (
                this.document || this.ownerDocument
            ).querySelectorAll(s);
            let i;
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            let el = this;
            do {
                i = matches.length;
                // eslint-disable-next-line no-empty
                while (--i >= 0 && matches.item(i) !== el) { }
            } while (i < 0 && (el = el.parentElement));
            return el;
        };
    }
}

/**
 * Smooth scroll to element on page
 *
 * @param {string|null} elementsSelector string -- css selector (anchor links)
 * @param {Function|null} callback function     -- callback for some additional actions
 * @param {number} offset function              -- offset in px
 */

export function anchorLinkScroll(
    elementsSelector = null,
    callback = null,
    offset = 0
) {
    if (!elementsSelector) {
        throw Error(
            '"anchorLinkScroll function - "You didn\'t add correct selector for anchor links'
        );
    }

    const elements = document.querySelectorAll(elementsSelector);

    elements &&
        [...elements].forEach((link) => {
            link.addEventListener('click', (event) => {
                event.preventDefault();

                const elHref =
                    event.target.nodeName === 'A'
                        ? event.target.getAttribute('href')
                        : event.target.dataset.href;

                if (!elHref.startsWith('#')) {
                    window.location = elHref;
                }

                const ANCHOR_ELEMENT = document.querySelector(elHref);

                ANCHOR_ELEMENT &&
                    window.scroll({
                        behavior: 'smooth',
                        left: 0,
                        top: ANCHOR_ELEMENT.offsetTop + offset,
                    });

                if (callback) callback(event);
            });
        });
}

export const setHeightEqualToWidth = (elementSelector) => {
    const elements = document.querySelectorAll(elementSelector);
    // @ts-ignore
    elements &&
        elements.forEach((element) => {
            const width = element.offsetWidth;
            // eslint-disable-next-line no-param-reassign
            element.style.height = `${width}px`;
        });
};

export const loadFileName = () => {
    const inputFileBtn = document.querySelector('.js-file-button');
    if (!inputFileBtn) return;
    const inputFile = inputFileBtn.querySelector('input');
    const spanText = inputFileBtn.querySelector('span');

    inputFile &&
        inputFile.addEventListener('change', () => {
            if (spanText) {
                spanText.innerText = inputFile.files[0].name;
            }
        });
};

export const setCookie = (name, value, hours) => {
    let expires = '';
    if (hours) {
        const date = new Date();
        date.setTime(date.getTime() + hours * 60 * 60 * 1000);
        expires = `expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value || ''};${expires};path=/`;
};

export const getCookie = (name) => {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) === 0) {
            return c.substring(nameEQ.length, c.length);
        }
    }
    return null;
};

export const primaryMenu = () => {
    const toggleMainMenuBtn = document.querySelectorAll('.js-toggle-menu-btn');
    const toggleMainMenuWordpressBtn = document.querySelectorAll('.menu-item');

    toggleMainMenuWordpressBtn && toggleMainMenuWordpressBtn.forEach(item => {
        item.addEventListener('click', (e) => {
            const toggleElement = document.querySelectorAll('.js-toggle-menu-elem')

            toggleElement && toggleElement.forEach(el => {
                el.classList.remove('menu-opened');
            })
        });
    })

    toggleMainMenuBtn && toggleMainMenuBtn.forEach(item => {
        item.addEventListener('click', (e) => {
            const toggleElement = document.querySelectorAll('.js-toggle-menu-elem')

            toggleElement && toggleElement.forEach(el => {
                el.classList.toggle('menu-opened');
            })
        });
    })
}

export const arrowUpBtn = () => {
    const arrowUp = document.querySelector('.arrow-up');

    function toggleArrowUp() {
        if (window.scrollY > 300) {
            arrowUp.classList.add('show');
        } else {
            arrowUp.classList.remove('show');
        }
    }

    window.addEventListener('scroll', toggleArrowUp);

    arrowUp.addEventListener('click', () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    });

}


export const dynamicHight = (selector) => {
    const wrappers = document.querySelectorAll(selector);
    let maxHeight = 0;

    wrappers.forEach(wrapper => {
        const totalHeight = wrapper.scrollHeight;
        if (totalHeight > maxHeight) {
            maxHeight = totalHeight;
        }
    });

    wrappers.forEach(wrapper => {
        wrapper.style.height = maxHeight + 'px';
    });
};

export const donationTextSlider = () => {
    document.querySelectorAll('.js-donation-text-slider').forEach((el) => {
        new Swiper(el, {
            modules: [Autoplay],
            spaceBetween: 10,
            slidesPerView: 1,
            direction: 'horizontal',
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
            },
        });
    });
};

export const initPhoneMask = () => {
    document.querySelectorAll('.js-phone-mask').forEach(input => {
        input.addEventListener('input', () => {
            let value = input.value.replace(/[^\d]/g, '');
            if (value.length > 11) value = value.slice(0, 11);

            if (value.length === 0) {
                input.value = '';
                return;
            }

            let formatted = '+7';
            if (value.length > 1) {
                formatted += ' (' + value.slice(1, 4);
            }
            if (value.length > 4) {
                formatted += ') ' + value.slice(4, 7);
            }
            if (value.length > 7) {
                formatted += '-' + value.slice(7, 9);
            }
            if (value.length > 9) {
                formatted += '-' + value.slice(9, 11);
            }

            input.value = formatted;
        });
    });
};

/**
 * Collect donation form fields and launch the payment widget on submit
 *
 * @param {string} formSelector           - selector of the donation form
 * @param {Object} options                - handler config
 * @param {Object} options.widget         - TipTopPaymentWidget instance
 * @param {Object} options.metadata       - base metadata for Salesforce
 * @param {Object} options.validator      - FormValidator instance used as a guard
 * @param {boolean} options.isSubscription - force subscription mode
 * @param {Function} options.onSuccess    - called after a successful payment
 */
export const donationFormSubmitHandler = (formSelector, options = {}) => {
    const form = document.querySelector(formSelector);
    if (!form || !options.widget) return;

    form.addEventListener('submit', (e) => {
        e.preventDefault();

        // This listener is attached before FormValidator's own one, so its preventDefault
        // would come too late — validity has to be checked here, before launching the widget
        if (options.validator && !options.validator.validateAll()) return;

        const formData = new FormData(e.target);
        const fields = Object.fromEntries(formData.entries());

        let amount = 0;

        if (fields['custom-pay-sum']) {
            amount = parseFloat(fields['custom-pay-sum'].replace(/[^0-9]/g, ''));
        } else {
            amount = parseFloat(fields['pay-sum'].replace(/[^0-9]/g, ''));
        }

        const paymentData = {
            amount: amount,
            isSubscription: options.isSubscription !== undefined ? options.isSubscription : fields['pay-period'] === 'monthly',
            accountId: fields.email || '',
            email: fields.email || '',
            userInfo: {
                accountId: fields.email,
                firstName: fields['first-name'] || '',
                email: fields.email || '',
                lastName: `${fields['last-name'] || ''}`,
                phone: fields.phone || '',
                birth: fields['birth-date'] || '',
            }
        };

        // Per-block terminal override; the attribute is printed only when the block sets one
        if (form.dataset.ttTerminal) {
            paymentData.customTerminalId = form.dataset.ttTerminal;
        }

        // Build metadata: base from options + dynamic fields
        paymentData.metadata = { ...(options.metadata || {}) };

        // Donor personal data for Salesforce
        if (fields['first-name']) paymentData.metadata.firstName = fields['first-name'];
        if (fields['last-name'])  paymentData.metadata.lastName  = fields['last-name'];
        if (fields.phone)         paymentData.metadata.phone     = fields.phone;
        if (fields['birth-date']) paymentData.metadata.birth     = fields['birth-date'];

        // Agent ID
        const agentSelect = form.querySelector('.js-sf-agent-select');
        if (agentSelect && agentSelect.value) {
            paymentData.metadata.agent_id = agentSelect.value;
        }

        // Venue ID
        const venueSelect = form.querySelector('.js-sf-venue-select');
        if (venueSelect && venueSelect.value) {
            paymentData.metadata.venue_id = venueSelect.value;
        }

        options.widget.launch(paymentData, paymentData.isSubscription, (result) => {
            if (typeof options.onSuccess === 'function') {
                options.onSuccess(result);
            }
        });
    });
};
